/**
 * AdSlot — the app-side wrapper that renders a sponsored ad inside a SANDBOXED
 * iframe pointed at the ad origin. See docs/AD_SERVING_AND_CONSOLE.md.
 *
 * Security: the iframe is sandboxed WITHOUT `allow-same-origin` (so the frame is a
 * null origin and cannot read this app's DOM, cookies, or localStorage) and WITHOUT
 * `allow-top-navigation` (it can't hijack the tab — clicks open in a new tab via
 * `allow-popups`). The only thing this component sends the ad origin is the placement
 * code + coarse, non-sensitive context (plan tier). No tokens, no PII.
 *
 * The frame reports its rendered height + fill status back via postMessage; we expand
 * to fit, or collapse to zero height when unfilled (no layout shift, no empty box).
 */
import React, { useEffect, useRef, useState } from 'react';
import './AdSlot.css';

const AD_ORIGIN = process.env.REACT_APP_AD_ORIGIN || 'http://localhost:5000';

interface AdSlotProps {
  /** Placement code, e.g. "music_list_interstitial". Must be a consumer-module slot. */
  placement: string;
  /** Coarse targeting only — the user's plan tier (free/basic/pro/…). Optional. */
  planTier?: string;
  className?: string;
}

interface AdSlotMessage {
  __adslot: true;
  filled: boolean;
  height: number;
}

function isAdSlotMessage(data: unknown): data is AdSlotMessage {
  return !!data && typeof data === 'object' && (data as any).__adslot === true;
}

export const AdSlot: React.FC<AdSlotProps> = ({ placement, planTier, className }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [filled, setFilled] = useState(false);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Sandboxed frame has a null origin, so we can't match on e.origin — instead
      // verify the message actually came from OUR iframe's window.
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      if (!isAdSlotMessage(e.data)) return;
      const h = Math.max(0, Math.min(600, Number(e.data.height) || 0));
      setFilled(!!e.data.filled);
      setHeight(e.data.filled ? h : 0);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const params = new URLSearchParams({ placement });
  if (planTier) params.set('plan', planTier);
  const src = `${AD_ORIGIN}/ad/frame?${params.toString()}`;

  return (
    <div
      className={`adslot ${filled ? 'adslot-filled' : 'adslot-empty'} ${className || ''}`}
      aria-hidden={!filled}
    >
      {filled && <span className="adslot-label">Sponsored</span>}
      <iframe
        ref={frameRef}
        title="Sponsored content"
        src={src}
        scrolling="no"
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        style={{ height: height ? `${height}px` : '0px' }}
        className="adslot-frame"
      />
    </div>
  );
};

export default AdSlot;
